import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../api'
import { Tenant, User } from '../types'

interface Props {
  tenant: Tenant
  currentUser: User
}

export function TenantUsersView({ tenant, currentUser }: Props) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Add User Modal State
  const [showAddModal, setShowAddModal] = useState(false)
  const [addFullName, setAddFullName] = useState('')
  const [addEmail, setAddEmail] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  // Edit User Modal State
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editFullName, setEditFullName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editPassword, setEditPassword] = useState('')
  const [editIsActive, setEditIsActive] = useState(true)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.listTenantUsers(tenant.id)
      setUsers(data)
    } catch (err: any) {
      setError(err.message || 'Kullanıcı listesi alınamadı.')
    } finally {
      setLoading(false)
    }
  }, [tenant.id])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleOpenAddModal = () => {
    setAddFullName('')
    setAddEmail('')
    setAddPassword('')
    setAddError(null)
    setShowAddModal(true)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addEmail.trim() || !addPassword) {
      setAddError('E-posta ve şifre zorunludur.')
      return
    }
    if (addPassword.length < 6) {
      setAddError('Şifre en az 6 karakter olmalıdır.')
      return
    }

    setAddLoading(true)
    setAddError(null)

    try {
      await api.createTenantUser(tenant.id, {
        email: addEmail.trim(),
        password: addPassword,
        full_name: addFullName.trim() || undefined,
      })
      setShowAddModal(false)
      setSuccessMessage('Yeni kullanıcı başarıyla oluşturuldu.')
      fetchUsers()
    } catch (err: any) {
      setAddError(err.message || 'Kullanıcı oluşturulurken bir hata oluştu.')
    } finally {
      setAddLoading(false)
    }
  }

  const handleOpenEditModal = (u: User) => {
    setEditingUser(u)
    setEditFullName(u.full_name || '')
    setEditEmail(u.email)
    setEditPassword('')
    setEditIsActive(u.is_active)
    setEditError(null)
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    if (!editEmail.trim()) {
      setEditError('E-posta adresi boş bırakılamaz.')
      return
    }
    if (editPassword && editPassword.length < 6) {
      setEditError('Yeni şifre en az 6 karakter olmalıdır.')
      return
    }

    setEditLoading(true)
    setEditError(null)

    try {
      await api.updateTenantUser(tenant.id, editingUser.id, {
        full_name: editFullName.trim(),
        email: editEmail.trim(),
        password: editPassword ? editPassword : undefined,
        is_active: editIsActive,
      })
      setEditingUser(null)
      setSuccessMessage('Kullanıcı bilgileri güncellendi.')
      fetchUsers()
    } catch (err: any) {
      setEditError(err.message || 'Kullanıcı güncellenirken bir hata oluştu.')
    } finally {
      setEditLoading(false)
    }
  }

  const handleToggleActive = async (u: User) => {
    if (u.role === 'tenant_admin') {
      alert('Yönetici hesaplarının durumunu doğrudan değiştiremezsiniz.')
      return
    }

    const actionText = u.is_active ? 'pasif' : 'aktif'
    if (!window.confirm(`"${u.full_name || u.email}" kullanıcısını ${actionText} duruma getirmek istiyor musunuz?`)) {
      return
    }

    try {
      await api.updateTenantUser(tenant.id, u.id, { is_active: !u.is_active })
      setSuccessMessage(`Kullanıcı durumu "${actionText}" olarak güncellendi.`)
      fetchUsers()
    } catch (err: any) {
      alert(err.message || 'Durum değiştirilemedi.')
    }
  }

  const handleDeleteUser = async (u: User) => {
    if (u.role === 'tenant_admin') {
      alert('Yönetici hesapları silinemez.')
      return
    }
    if (u.id === currentUser.id) {
      alert('Kendi hesabınızı silemezsiniz.')
      return
    }

    if (!window.confirm(`"${u.full_name || u.email}" kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
      return
    }

    const itemToDelete = users.find((user) => user.id === u.id)
    const originalIndex = users.findIndex((user) => user.id === u.id)

    setUsers((prev) => prev.filter((user) => user.id !== u.id))

    try {
      await api.deleteTenantUser(tenant.id, u.id)
      setSuccessMessage('Kullanıcı başarıyla silindi.')
      fetchUsers()
    } catch (err: any) {
      if (itemToDelete) {
        setUsers((prev) => {
          if (prev.some((user) => user.id === u.id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      alert(err.message || 'Kullanıcı silinemedi.')
    }
  }

  const getInitials = (u: User) => {
    if (u.full_name) {
      return u.full_name
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return u.email.slice(0, 2).toUpperCase()
  }

  const filteredUsers = users.filter((u) => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return true
    const matchEmail = u.email.toLowerCase().includes(q)
    const matchName = u.full_name ? u.full_name.toLowerCase().includes(q) : false
    return matchEmail || matchName
  })

  return (
    <div className="tenant-users-container">
      {successMessage && (
        <div className="alert alert-success" role="status" style={{ marginBottom: '1.25rem' }}>
          <span>✓ {successMessage}</span>
          <button
            type="button"
            className="btn-link"
            style={{ marginLeft: 'auto', textDecoration: 'none' }}
            onClick={() => setSuccessMessage(null)}
          >
            ✕
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: '1.25rem' }}>
          <span>✕ {error}</span>
          <button
            type="button"
            className="btn-link"
            style={{ marginLeft: 'auto', textDecoration: 'none' }}
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="table-toolbar" style={{ marginBottom: '1rem' }}>
        <div className="table-search-box">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            placeholder="İsim veya e-posta ile kullanıcı ara…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="table-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            Toplam: <strong>{users.length}</strong> kullanıcı
          </span>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenAddModal}
            id="btn-add-tenant-user"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ marginRight: '6px' }}
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Yeni Kullanıcı Ekle
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-card">
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 1rem' }} aria-hidden="true" />
            <span>Kullanıcı listesi yükleniyor…</span>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            {searchQuery ? (
              <p>Arama kriterine uygun kullanıcı bulunamadı.</p>
            ) : (
              <p>Henüz kayıtlı standart kullanıcı bulunmuyor.</p>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Kullanıcı Bilgisi</th>
                <th>E-Posta</th>
                <th style={{ width: '130px' }}>Rol</th>
                <th style={{ width: '110px' }}>Durum</th>
                <th style={{ width: '130px' }}>Kayıt Tarihi</th>
                <th style={{ width: '160px', textAlign: 'right' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isAdmin = u.role === 'tenant_admin'
                const isSelf = u.id === currentUser.id
                const canEdit = !isAdmin || isSelf
                const canDelete = !isAdmin && !isSelf
                const initials = getInitials(u)

                return (
                  <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                    <td>
                      <div className="user-avatar-circle" aria-hidden="true">
                        {initials}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                        {u.full_name || 'İsimsiz Kullanıcı'}
                        {isSelf && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'var(--color-primary)',
                              backgroundColor: 'var(--color-primary-light)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            Siz
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                      {u.email}
                    </td>
                    <td>
                      {isAdmin ? (
                        <span className="badge badge-admin">Yönetici</span>
                      ) : (
                        <span className="badge badge-user">Kullanıcı</span>
                      )}
                    </td>
                    <td>
                      {u.is_active ? (
                        <span className="badge badge-success">Aktif</span>
                      ) : (
                        <span className="badge badge-warning">Pasif</span>
                      )}
                    </td>
                    <td className="tabular-nums text-muted" style={{ fontSize: '0.8125rem' }}>
                      {u.created_at
                        ? new Date(u.created_at).toLocaleDateString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                        {/* Edit Button */}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleOpenEditModal(u)}
                          disabled={!canEdit}
                          title={
                            isAdmin && !isSelf
                              ? 'Diğer yöneticilerin bilgileri düzenlenemez'
                              : 'Kullanıcıyı Düzenle'
                          }
                        >
                          Düzenle
                        </button>

                        {/* Quick Toggle Active Button */}
                        {!isAdmin && (
                          <button
                            type="button"
                            className={`btn btn-sm ${u.is_active ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => handleToggleActive(u)}
                            title={u.is_active ? 'Kullanıcıyı pasife al' : 'Kullanıcıyı aktifleştir'}
                          >
                            {u.is_active ? 'Pasif Yap' : 'Aktifleştir'}
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteUser(u)}
                          disabled={!canDelete}
                          title={
                            isAdmin
                              ? 'Yönetici hesapları silinemez'
                              : isSelf
                              ? 'Kendi hesabınızı silemezsiniz'
                              : 'Kullanıcıyı Sil'
                          }
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-backdrop" onClick={() => !addLoading && setShowAddModal(false)}>
          <div className="modal-card modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Yeni Kullanıcı Ekle</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
                disabled={addLoading}
                aria-label="Kapat"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {addError && (
                  <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                    ✕ {addError}
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-user-name">Ad Soyad</label>
                  <input
                    id="add-user-name"
                    type="text"
                    placeholder="Örn: Ahmet Yılmaz"
                    value={addFullName}
                    onChange={(e) => setAddFullName(e.target.value)}
                    disabled={addLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-user-email">E-posta Adresi *</label>
                  <input
                    id="add-user-email"
                    type="email"
                    required
                    placeholder="ornek@firma.com"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    disabled={addLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-user-password">Giriş Şifresi * (En az 6 karakter)</label>
                  <input
                    id="add-user-password"
                    type="password"
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={addPassword}
                    onChange={(e) => setAddPassword(e.target.value)}
                    disabled={addLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Kullanıcı Rolü</label>
                  <input
                    type="text"
                    value="Standart Kullanıcı (tenant_user)"
                    disabled
                    style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text-muted)' }}
                  />
                  <span className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '4px', display: 'block' }}>
                    Tenant yöneticileri yalnızca standart kullanıcı yetkisi atayabilir.
                  </span>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={addLoading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={addLoading} id="btn-submit-add-user">
                  {addLoading ? 'Kaydediliyor…' : 'Kullanıcıyı Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-backdrop" onClick={() => !editLoading && setEditingUser(null)}>
          <div className="modal-card modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Kullanıcıyı Düzenle</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setEditingUser(null)}
                disabled={editLoading}
                aria-label="Kapat"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                {editError && (
                  <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                    ✕ {editError}
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="edit-user-name">Ad Soyad</label>
                  <input
                    id="edit-user-name"
                    type="text"
                    placeholder="Örn: Ahmet Yılmaz"
                    value={editFullName}
                    onChange={(e) => setEditFullName(e.target.value)}
                    disabled={editLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="edit-user-email">E-posta Adresi *</label>
                  <input
                    id="edit-user-email"
                    type="email"
                    required
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    disabled={editLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="edit-user-password">Yeni Şifre Belirle (İsteğe Bağlı)</label>
                  <input
                    id="edit-user-password"
                    type="password"
                    minLength={6}
                    placeholder="Mevcut şifreyi korumak için boş bırakın"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    disabled={editLoading}
                  />
                </div>

                {editingUser.role !== 'tenant_admin' && (
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editIsActive}
                        onChange={(e) => setEditIsActive(e.target.checked)}
                        disabled={editLoading}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: 500 }}>Hesap Aktif</span>
                    </label>
                    <span className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '2px', display: 'block' }}>
                      Hesap pasife alındığında kullanıcı sisteme giriş yapamaz.
                    </span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingUser(null)}
                  disabled={editLoading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading} id="btn-submit-edit-user">
                  {editLoading ? 'Güncelleniyor…' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
